import { useQueries, useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import {
  CRITERION_MARK_SYMBOLS,
  RANKING_KEYWORDS_MAX,
  normaliseKeyword,
  sameKeyword,
  type CorrectCriterionRequest,
  type CriterionCorrection,
  type CriterionMark,
  type RankingDetail,
  type RankingKeywordMatches,
  type RankingRow,
  type RankingSuggestions,
  type RankingView,
} from '@nest-recruit/shared';
import { apiGet, apiPost, errorMessage, type ApiError } from '../../../lib/api-client';
import { createUserPreferenceStore } from '../../../lib/preferences';
import { useCurrentSession } from '../../../stores/session';
import { useToasts, type Toast } from '../../../stores/toasts';
import { withMark } from '../ranking-view';

// RNK-01 and RNK-02 (R5). Both sit under `['applications']`, the key every ranking, move and
// import hint already names, so the screen follows the server with no polling (#12).

export function rankingKey(jobId: string): readonly unknown[] {
  return ['applications', 'ranking', jobId];
}

export function rankingDetailKey(applicationId: string): readonly unknown[] {
  return ['applications', 'ranking-detail', applicationId];
}

export function useRanking(jobId: string): UseQueryResult<RankingView, ApiError> {
  return useQuery<RankingView, ApiError>({
    queryKey: rankingKey(jobId),
    queryFn: () => apiGet<RankingView>(`/jobs/${jobId}/ranking`),
    // CLAUDE.md §5a rule 2: a re-rank or a hint keeps the old order on screen until the new one lands.
    placeholderData: (previous) => previous,
  });
}

export function useRankingDetail(applicationId: string | null): UseQueryResult<RankingDetail, ApiError> {
  return useQuery<RankingDetail, ApiError>({
    queryKey: rankingDetailKey(applicationId ?? ''),
    queryFn: () => apiGet<RankingDetail>(`/applications/${applicationId ?? ''}/ranking`),
    enabled: applicationId !== null,
    placeholderData: (previous) => (previous?.applicationId === applicationId ? previous : undefined),
  });
}

/** R6: the review card fetches the next three cards' breakdowns while this one is read (F8.02). */
export function prefetchRankingDetail(queryClient: QueryClient, applicationId: string): void {
  void queryClient.prefetchQuery({ queryKey: rankingDetailKey(applicationId), queryFn: () => apiGet<RankingDetail>(`/applications/${applicationId}/ranking`) });
}

/** What a mark goes back to: a person's earlier ✓/✗, or the machine's own (null). */
export interface MarkTarget {
  readonly mark: 'met' | 'not_met' | null;
  /** The mark and match to draw meanwhile — for null, the machine's. */
  readonly shownMark: CriterionMark;
  readonly shownMatch: number;
}

interface CorrectionDeps {
  readonly queryClient: QueryClient;
  readonly jobId: string;
  /** Who is correcting, for the mark drawn before the server answers ("Set to ✓ by …"). */
  readonly by: { readonly id: string; readonly name: string };
  readonly show: (toast: Omit<Toast, 'id' | 'durationMs'>) => string;
  readonly showUndo: (message: string, undo: () => void) => string;
  readonly setPendingKey: (key: string | null) => void;
}

/**
 * F23.05 with the instant feel (CLAUDE.md §5a): the cell, the score and the tab change at
 * once, the server confirms, and only when the refetch has landed does the optimistic state
 * let go. A refusal puts everything back and says why. Every change offers Undo for 10 s.
 */
export function useCorrectCriterion(jobId: string): {
  readonly correct: (detail: RankingDetail, key: string, target: MarkTarget) => Promise<void>;
  /** F23.05's optional note on a person's ✓/✗, added or changed after the mark (Redesign Group 3); null takes it away. */
  readonly saveNote: (detail: RankingDetail, key: string, note: string | null) => Promise<void>;
  readonly pendingKey: string | null;
} {
  const queryClient = useQueryClient();
  const session = useCurrentSession();
  const userId = session?.id ?? '';
  const userName = session?.fullName ?? 'You';
  const show = useToasts((toasts) => toasts.show);
  const showUndo = useToasts((toasts) => toasts.showUndo);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const deps = useMemo<CorrectionDeps>(
    () => ({ queryClient, jobId, by: { id: userId, name: userName }, show, showUndo, setPendingKey }),
    [queryClient, jobId, userId, userName, show, showUndo],
  );
  const correct = useCallback((detail: RankingDetail, key: string, target: MarkTarget): Promise<void> => applyMark(deps, detail, key, target, false), [deps]);
  const saveNote = useCallback((detail: RankingDetail, key: string, note: string | null): Promise<void> => applyNote(deps, detail, key, note), [deps]);
  return { correct, saveNote, pendingKey };
}

/** The toast's "Score 62 → 71." from the server's answer; nothing when either side isn't ranked. */
export function scoreChange(before: number | null, after: number | null): string {
  if (before === null || after === null) return '';
  return before === after ? ` Score stays ${after}.` : ` Score ${before} → ${after}.`;
}

/** The sheet as it will be once the server agrees: the mark, its points, and the row's new score, stars and tab. */
function optimisticDetail(detail: RankingDetail, key: string, target: MarkTarget, by: CorrectionDeps['by'], row: RankingRow | undefined): RankingDetail {
  const matches = detail.matches.map((match) => {
    if (match.key !== key) return match;
    const correction: CriterionCorrection | null =
      target.mark === null ? null : { mark: target.mark, note: null, byUserId: by.id, byName: by.name, at: new Date().toISOString(), criteriaVersion: match.correction?.criteriaVersion ?? 0 };
    return { ...match, mark: target.shownMark, match: target.shownMatch, points: match.weight * target.shownMatch, correction };
  });
  if (!row) return { ...detail, matches };
  return { ...detail, matches, score: row.score, stars: row.stars, tab: row.tab, coverage: row.coverage, missingMustHaves: row.missingMustHaves };
}

async function applyMark(deps: CorrectionDeps, detail: RankingDetail, key: string, target: MarkTarget, isUndo: boolean): Promise<void> {
  const { queryClient } = deps;
  const listKey = rankingKey(deps.jobId);
  const detailKey = rankingDetailKey(detail.applicationId);
  const listBefore = queryClient.getQueryData<RankingView>(listKey);
  const detailBefore = queryClient.getQueryData<RankingDetail>(detailKey) ?? detail;
  const criterion = detailBefore.matches.find((match) => match.key === key);
  if (!criterion) return;
  const previous: MarkTarget = criterion.correction
    ? { mark: criterion.correction.mark, shownMark: criterion.correction.mark, shownMatch: criterion.match }
    : { mark: null, shownMark: criterion.machineMark, shownMatch: criterion.machineMatch };

  await queryClient.cancelQueries({ queryKey: listKey });
  // The mark changes in the list and on the sheet at once (#11); nothing waits for the server.
  const marked = listBefore?.rows.find((row) => row.applicationId === detail.applicationId);
  const markedRow = marked && listBefore ? withMark(marked, listBefore.criteria, key, target.shownMark, target.shownMatch, target.mark !== null) : undefined;
  if (listBefore && markedRow) queryClient.setQueryData<RankingView>(listKey, { ...listBefore, rows: listBefore.rows.map((row) => (row.applicationId === detail.applicationId ? markedRow : row)) });
  queryClient.setQueryData<RankingDetail>(detailKey, optimisticDetail(detailBefore, key, target, deps.by, markedRow));
  deps.setPendingKey(key);
  try {
    const body: CorrectCriterionRequest = { mark: target.mark, note: null, version: detailBefore.version };
    const saved = await apiPost<RankingDetail>(`/applications/${detail.applicationId}/criteria/${key}/mark`, body);
    queryClient.setQueryData(detailKey, saved);
    await queryClient.invalidateQueries({ queryKey: listKey });
    if (isUndo) return;
    const symbol = target.mark === null ? "the ranking's mark" : CRITERION_MARK_SYMBOLS[target.mark];
    deps.showUndo(`${detail.name}: ${criterion.label} set to ${symbol}.${scoreChange(detailBefore.score, saved.score)}`, () => {
      const latest = queryClient.getQueryData<RankingDetail>(detailKey) ?? saved;
      void applyMark(deps, latest, key, previous, true);
    });
  } catch (error) {
    if (listBefore) queryClient.setQueryData(listKey, listBefore);
    queryClient.setQueryData(detailKey, detailBefore);
    deps.show({ tone: 'danger', message: `${criterion.label} stayed as it was`, detail: errorMessage(error) });
    await queryClient.invalidateQueries({ queryKey: detailKey });
  } finally {
    deps.setPendingKey(null);
  }
}

/** The note shows at once; a refusal takes it back and says why (CLAUDE.md §5a). A note changes no score. */
async function applyNote(deps: CorrectionDeps, detail: RankingDetail, key: string, note: string | null): Promise<void> {
  const { queryClient } = deps;
  const detailKey = rankingDetailKey(detail.applicationId);
  const before = queryClient.getQueryData<RankingDetail>(detailKey) ?? detail;
  const criterion = before.matches.find((match) => match.key === key);
  const correction = criterion?.correction;
  if (!criterion || !correction) return;
  queryClient.setQueryData<RankingDetail>(detailKey, { ...before, matches: before.matches.map((match) => (match.key === key ? { ...match, correction: { ...correction, note } } : match)) });
  try {
    const body: CorrectCriterionRequest = { mark: correction.mark, note, version: before.version };
    const saved = await apiPost<RankingDetail>(`/applications/${detail.applicationId}/criteria/${key}/mark`, body);
    queryClient.setQueryData(detailKey, saved);
    await queryClient.invalidateQueries({ queryKey: rankingKey(deps.jobId) });
  } catch (error) {
    queryClient.setQueryData(detailKey, before);
    deps.show({ tone: 'danger', message: `The note on ${criterion.label} wasn't saved`, detail: errorMessage(error) });
  }
}

// --- F23.09: keyword search, a filter only (ADR 0048) ---------------------------------------

export function rankingKeywordKey(jobId: string, word: string): readonly unknown[] {
  return ['applications', 'ranking-keyword', jobId, normaliseKeyword(word).toLowerCase()];
}

/** The keywords stay when the viewer leaves and comes back (Q3), per person and per job. */
export function useRankingKeywords(jobId: string): { readonly keywords: readonly string[]; readonly setKeywords: (next: readonly string[]) => void } {
  const userId = useCurrentSession()?.id ?? 'anonymous';
  const store = useMemo(() => createUserPreferenceStore(userId), [userId]);
  const key = `ranking.keywords.${jobId}`;
  const [keywords, setState] = useState<readonly string[]>(() => storedKeywords(store.get<unknown>(key, [])));
  const setKeywords = useCallback(
    (next: readonly string[]): void => {
      const unique = next.map(normaliseKeyword).filter((word, index, all) => word !== '' && all.findIndex((other) => sameKeyword(other, word)) === index).slice(0, RANKING_KEYWORDS_MAX);
      setState(unique);
      if (unique.length === 0) store.remove(key);
      else store.set(key, unique);
    },
    [store, key],
  );
  return { keywords, setKeywords };
}

function storedKeywords(value: unknown): readonly string[] {
  return Array.isArray(value) ? value.filter((word): word is string => typeof word === 'string').slice(0, RANKING_KEYWORDS_MAX) : [];
}

/**
 * One request per keyword, cached on its own, so removing one is instant and adding one asks only
 * for it. Each entry is that keyword's matching application ids, or undefined while it loads; the
 * list keeps its identity between renders while nothing changes.
 */
export function useKeywordMatches(jobId: string, keywords: readonly string[]): readonly (readonly string[] | undefined)[] {
  return useQueries({
    queries: keywords.map((word) => ({
      queryKey: rankingKeywordKey(jobId, word),
      queryFn: () => apiGet<RankingKeywordMatches>(`/jobs/${jobId}/ranking/keyword`, { query: { word } }),
      staleTime: KEYWORD_STALE_MS,
    })),
    combine: (results) => results.map((result) => result.data?.applicationIds),
  });
}

const KEYWORD_STALE_MS = 60_000;

// --- F23.12: suggested keywords (ADR 0049) ---------------------------------------------------

/**
 * Not under `['applications']`: every import and re-rank hint would re-read every CV of the job. The words
 * change slowly, so five minutes is fresh enough, and a page reload asks again.
 */
export function useKeywordSuggestions(jobId: string): UseQueryResult<RankingSuggestions, ApiError> {
  return useQuery<RankingSuggestions, ApiError>({
    queryKey: ['ranking-suggestions', jobId],
    queryFn: () => apiGet<RankingSuggestions>(`/jobs/${jobId}/ranking/suggestions`),
    staleTime: SUGGESTIONS_STALE_MS,
    placeholderData: (previous) => (previous?.jobId === jobId ? previous : undefined),
  });
}

const SUGGESTIONS_STALE_MS = 300_000;
